import { View, Text, StyleSheet } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import {
  AUDIO_LANGUAGES,
  Colors,
  SELECT_STYLES,
  SUPPORTED_MEDIA_FILE_EXTENSIONS,
} from "@/constants";
import PrimaryButton from "./PrimaryButton";
import { useState } from "react";
import useFileSelector from "@/models/useFileSelector";
import toast from "react-hot-toast";
import { useUserSlice } from "@/models/useUserSlice";
import { useRouter } from "@/_router";
import { getBasePath } from "@/models/utils";

const AlertWidgetCreateNoteMediaImport = () => {
  const visible = useUISlice((state) => state.alertWidget === "media_import");
  const setVisible = useUISlice((state) => state.setAlertWidget);
  const { openFilePicker } = useFileSelector();
  const router = useRouter();
  const width = useUISlice((state) => state.device.width);
  const transcriptionLanguage = useUserSlice(
    (state) => state.userData?.preferences?.transcriptionLanguage
  );
  const [language, setLanguage] = useState<string>(
    transcriptionLanguage || AUDIO_LANGUAGES[0].value
  );
  const { t } = useTranslation();

  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const folders = useUserSlice((state) => {
    if (state?.folders?.length === 0) return undefined;
    return [
      { id: undefined, name: t("alertWidgetCreateNoteMediaImport.allNotes") },
      ...state.folders,
    ];
  });

  if (!visible) return null;

  const onPress = async () => {
    const file = await openFilePicker(SUPPORTED_MEDIA_FILE_EXTENSIONS);

    if (!file) return;

    const isValidFile = () => {
      const extension = `.${file.name.split(".").pop()}`.toLowerCase();
      const isAudioOrVideo =
        file.type.toLowerCase().includes("audio") ||
        file.type.toLowerCase().includes("video");
      return (
        SUPPORTED_MEDIA_FILE_EXTENSIONS.includes(extension) || isAudioOrVideo
      );
    };

    if (!isValidFile()) {
      toast.error(
        t("alertWidgetCreateNoteMediaImport.onlyAudioAndVideoFilesSupported")
      );
      setVisible(undefined);
      return;
    }

    useUserSlice
      .getState()
      .note()
      .create(
        {
          type: "media_import",
          name: file.name,
          status: "upload_started",
          transcriptionLanguage: language,
        },
        folderId,
        (note) => {
          useUISlice
            .getState()
            .setNoteScreenData({ upload_started: { file, language } });

          setVisible(undefined);

          router?.push(`${getBasePath()}/note?id=${note.id}`);
        }
      );
  };

  return (
    <ModalContainer
      title={t("alertWidgetCreateNoteMediaImport.title")}
      onDismiss={() => setVisible(undefined)}
      hideCancel
      modalWidth={Math.min(width, 600)}
    >
      <View style={styles.spacer} />

      <Text style={styles.label}>
        {t("alertWidgetCreateNoteMediaImport.audioLanguage")}
      </Text>
      <select
        style={SELECT_STYLES}
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
      >
        {AUDIO_LANGUAGES.map((language) => (
          <option key={language.value} value={language.value}>
            {language.label}
          </option>
        ))}
      </select>

      {folders && (
        <>
          <View style={styles.spacer} />
          <Text style={styles.label}>
            {t("alertWidgetCreateNoteMediaImport.folder")}
          </Text>
          <select
            style={SELECT_STYLES}
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
          >
            {folders.map((folder, index) => (
              <option key={index} value={folder.id}>
                {`📂 ${folder.name}`}
              </option>
            ))}
          </select>
        </>
      )}

      <View style={styles.largerSpacer} />

      <PrimaryButton
        icon="upload"
        text={t("alertWidgetCreateNoteMediaImport.importAudio")}
        onPress={onPress}
      />
      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: ms(16),
  },
  largerSpacer: {
    height: ms(48),
  },
  label: {
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text,
    marginBottom: ms(8),
  },
  negativeMargin: {
    marginBottom: -ms(16),
  },
});

export default AlertWidgetCreateNoteMediaImport;
